/* eslint-disable react/no-array-index-key */
/* ==============================================================================
  DashboardPosV2 - อนุมัติคำขอ POS (WebSocket) + รอบขายที่เปิดอยู่ (API)
============================================================================== */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import InputAdornment from '@mui/material/InputAdornment';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const WS_URL =
  process.env.NODE_ENV === 'development'
    ? 'ws://localhost:7000/ws/pos'
    : 'wss://pos.ruangthongpharmacy.com/service/ws/pos';
const API_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:7000'
    : 'https://pos.ruangthongpharmacy.com/service';
const RECONNECT_INTERVAL = 5000;

// MUI Chip รองรับเฉพาะสีเหล่านี้เท่านั้น ค่าอื่น ๆ จะทำให้ colorManipulator พัง
const CHIP_COLORS = ['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning'];
const safeChipColor = (color) => (CHIP_COLORS.includes(color) ? color : 'default');

const CATEGORY_META = {
  DelCart: {
    title: 'ขอลบสินค้า',
    icon: 'delete_outline',
    color: 'error',
    wsType: 'DEL_CART_LIST',
    emptyIcon: 'shopping_cart',
  },
  DiscountRequest: {
    title: 'ขอส่วนลด',
    icon: 'discount',
    color: 'warning',
    wsType: 'DISCOUNT_REQUEST',
    emptyIcon: 'local_offer',
  },
  CancelBill: {
    title: 'ยกเลิกบิล',
    icon: 'highlight_off',
    color: 'error',
    wsType: 'CANCEL_BILL',
    emptyIcon: 'receipt_long',
  },
  ReturnBill: {
    title: 'เปลี่ยน/คืนสินค้า',
    icon: 'change_circle',
    color: 'info',
    wsType: 'RETURN_BILL',
    emptyIcon: 'change_circle',
  },
  CloseShift: {
    title: 'ปิดรอบขาย',
    icon: 'point_of_sale',
    color: 'secondary',
    wsType: 'CLOSE_SHIFT',
    emptyIcon: 'lock_clock',
  },
};

const CATEGORY_KEYS = Object.keys(CATEGORY_META);

const formatMoney = (n) => {
  const v = Number(n ?? 0);
  return v.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (s) => {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return s;
  }
};

const flattenCategory = (bucket, isList = false) => {
  if (!bucket) return [];
  const out = [];
  Object.entries(bucket).forEach(([shiftId, value]) => {
    if (isList && Array.isArray(value)) {
      value.forEach((item) => out.push({ shiftId: Number(shiftId), item }));
    } else if (value) {
      out.push({ shiftId: Number(shiftId), item: value });
    }
  });
  return out;
};

function DashboardPosV2() {
  const mod = 'dashboard_pos_v2';
  const { t, i18n } = useTranslation();
  const app = useSelector((state) => state[APP_STATE]);

  const token = app?.user?.token || app?.token || '';

  const [snapshot, setSnapshot] = useState({
    DelCart: {},
    DiscountRequest: {},
    CancelBill: {},
    ReturnBill: {},
    CloseShift: {},
  });
  const [status, setStatus] = useState('connecting');
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [shifts, setShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [shiftSearch, setShiftSearch] = useState('');
  const [stockMinus, setStockMinus] = useState([]);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const isUnmountedRef = useRef(false);
  // กันไม่ให้ Skeleton กระโดดกลับมาตอน reconnect (เคยรับ snapshot แล้ว 1 ครั้ง)
  const hasLoadedRef = useRef(false);
  // hash ของ snapshot ล่าสุด เอาไว้กัน setSnapshot ซ้ำเมื่อข้อมูลเหมือนเดิม
  const lastSnapshotHashRef = useRef('');

  const showNotify = (msg, color = 'success') => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3500}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  // ===========================================
  // WebSocket - เชื่อมครั้งเดียวต่อ token, reconnect เฉพาะตอน connection ขาดจริง
  // ===========================================
  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return undefined;
    }

    isUnmountedRef.current = false;

    const openSocket = () => {
      // กัน race: ถ้ามี socket เก่ายัง OPEN/CONNECTING อยู่ ไม่ต้องเปิดใหม่
      const existing = wsRef.current;
      if (
        existing &&
        (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      let ws;
      try {
        const url = `${WS_URL}?token=${encodeURIComponent(token)}`;
        ws = new WebSocket(url);
      } catch (e) {
        console.error('ws connect error', e);
        setStatus('offline');
        return;
      }

      wsRef.current = ws;
      // ถ้ายังไม่เคยโหลดสำเร็จ ค่อยขึ้น "กำลังเชื่อมต่อ" — กันกระพริบ status ระหว่าง reconnect เงียบ ๆ
      if (!hasLoadedRef.current) setStatus('connecting');

      ws.onopen = () => setStatus('online');

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // รองรับทั้ง type/Type เพื่อกัน PING ทุกแบบ (ก่อนหน้านี้เช็คผิด case ทำให้ snapshot ถูก reset เป็น empty)
          const msgType = data?.type ?? data?.Type;
          if (msgType === 'PING') {
            // heartbeat: ตอบกลับทันทีเพื่อให้ server รู้ว่า client ยัง alive
            if (ws.readyState === WebSocket.OPEN) {
              try {
                ws.send(JSON.stringify({ type: 'PING', requestAt: new Date().toISOString() }));
              } catch (err) {
                console.error('ws send PING reply error', err);
              }
            }
            return;
          }

          // ถ้า payload ไม่มี field ของ category ใด ๆ ถือว่าไม่ใช่ snapshot — ไม่ต้องยุ่งกับ state
          const hasAnyCategory =
            data && CATEGORY_KEYS.some((k) => Object.prototype.hasOwnProperty.call(data, k));
          if (!hasAnyCategory) return;

          const next = {
            DelCart: data?.DelCart || {},
            DiscountRequest: data?.DiscountRequest || {},
            CancelBill: data?.CancelBill || {},
            ReturnBill: data?.ReturnBill || {},
            CloseShift: data?.CloseShift || {},
          };

          // กัน setSnapshot ซ้ำเมื่อ payload เหมือนเดิม → ไม่ re-render ทั้งหน้าโดยไม่จำเป็น
          const hash = JSON.stringify(next);
          if (hash !== lastSnapshotHashRef.current) {
            lastSnapshotHashRef.current = hash;
            setSnapshot(next);
          }
          hasLoadedRef.current = true;
        } catch (e) {
          console.error('parse ws message error', e, event.data);
        }
      };

      ws.onclose = (e) => {
        setStatus('offline');
        if (isUnmountedRef.current) return;
        if (e.code === 4001) {
          showNotify('Token ไม่ถูกต้องหรือหมดอายุ', 'error');
          return;
        }
        if (reconnectRef.current) clearTimeout(reconnectRef.current);
        reconnectRef.current = setTimeout(openSocket, RECONNECT_INTERVAL);
      };

      ws.onerror = () => setStatus('offline');
    };

    openSocket();

    return () => {
      isUnmountedRef.current = true;
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
      if (wsRef.current) {
        try {
          // ตัด handler ทิ้งก่อน close เพื่อไม่ให้ onclose เก่าไป schedule reconnect ทับของใหม่
          wsRef.current.onopen = null;
          wsRef.current.onmessage = null;
          wsRef.current.onclose = null;
          wsRef.current.onerror = null;
          wsRef.current.close();
        } catch {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
  }, [token]);

  const loadShifts = () => {
    setLoadingShifts(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      setLoadingShifts(false);

      if (result.status === 'ok') {
        setShifts(result.data || []);
      } else {
        showNotify(t('alert.loaderror') || 'โหลดข้อมูลรอบขายไม่สำเร็จ', 'error');
      }
    });
  };

  const loadStockMinus = () => {
    FetchData('OPTIONS', `/${mod}/stock_minus`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setStockMinus(result.data || []);
      }
    });
  };

  useEffect(() => {
    if (i18n && app?.lang) i18n.changeLanguage(app.lang);
    loadShifts();
    loadStockMinus();
  }, []);

  const displayShifts = useMemo(() => {
    const q = shiftSearch.trim().toLowerCase();
    if (!q) return shifts;

    return shifts.filter((row) => {
      const haystack = [row.branch_name, row.shift_code, row.pos, row.cashier_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [shifts, shiftSearch]);

  // ===========================================
  // Approve / Reject
  // ===========================================
  const callAction = async (category, action, payload) => {
    const key = `${category}:${action}:${JSON.stringify(payload)}`;
    const wsType = CATEGORY_META[category]?.wsType;
    if (!wsType) {
      showNotify(`ไม่รู้จักประเภท: ${category}`, 'error');
      return;
    }

    const body = {
      Type: wsType,
      Action: action === 'approve',
      ShiftId: Number(payload.shiftId),
      ApprovalId: payload.approvalId,
    };

    setBusyKey(key);
    try {
      const res = await fetch(`${API_URL}/api/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
      }
      showNotify(`${action === 'approve' ? 'อนุมัติ' : 'ปฏิเสธ'}สำเร็จ`, 'success');
    } catch (e) {
      showNotify(
        `ไม่สามารถ${action === 'approve' ? 'อนุมัติ' : 'ปฏิเสธ'}ได้: ${e.message}`,
        'error'
      );
    } finally {
      setBusyKey(null);
      setConfirm(null);
    }
  };

  const askConfirm = (category, action, payload, summary) => {
    setConfirm({ category, action, payload, summary });
  };

  // ===========================================
  // Derived data
  // ===========================================
  const lists = useMemo(
    () => ({
      DelCart: flattenCategory(snapshot.DelCart, true),
      DiscountRequest: flattenCategory(snapshot.DiscountRequest),
      CancelBill: flattenCategory(snapshot.CancelBill),
      ReturnBill: flattenCategory(snapshot.ReturnBill),
      CloseShift: flattenCategory(snapshot.CloseShift),
    }),
    [snapshot]
  );

  const counts = useMemo(
    () =>
      CATEGORY_KEYS.reduce((acc, k) => {
        acc[k] = lists[k].length;
        return acc;
      }, {}),
    [lists]
  );

  const total = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);

  const statusInfo = useMemo(() => {
    const map = {
      online: { label: 'เชื่อมต่อแล้ว', color: 'success', icon: 'wifi' },
      connecting: { label: 'กำลังเชื่อมต่อ...', color: 'info', icon: 'sync' },
      offline: { label: 'ขาดการเชื่อมต่อ', color: 'error', icon: 'wifi_off' },
      'no-token': { label: 'ไม่มี Token', color: 'warning', icon: 'vpn_key' },
    };
    return map[status] || map.offline;
  }, [status]);

  const reconnect = () => {
    // แค่ปิด socket เดิม — useEffect/onclose จะ schedule reconnect ให้เอง
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        /* ignore */
      }
    }
  };

  const renderStockMinusBanner = () => {
    if (!stockMinus.length) return null;

    return (
      <Card
        sx={{
          mb: 2.5,
          border: '2px solid',
          borderColor: 'error.main',
          bgcolor: 'rgba(244, 67, 53, 0.08)',
          boxShadow: (theme) =>
            `0 0 0 1px ${theme.palette.error.main}, 0 4px 20px rgba(211,47,47,0.25)`,
          '@keyframes stockMinusPulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.55 },
          },
        }}
      >
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: 'error.main', width: 44, height: 44 }}>
              <Icon>emergency</Icon>
            </Avatar>
          }
          title={
            <MDTypography variant='h6' fontWeight='bold' color='error'>
              สต็อกติดลบจากการขาย
            </MDTypography>
          }
          subheader={
            <MDTypography variant='button' color='error' fontWeight='medium'>
              พบ {stockMinus.length} รายการที่ขายแล้วสต็อกคงเหลือติดลบ
            </MDTypography>
          }
          action={
            <Tooltip title='โหลดใหม่'>
              <IconButton size='small' color='error' onClick={loadStockMinus}>
                <Icon>refresh</Icon>
              </IconButton>
            </Tooltip>
          }
          sx={{
            '& .MuiCardHeader-title': { lineHeight: 1.3 },
            borderBottom: '1px solid',
            borderColor: 'rgba(244, 67, 53, 0.35)',
            bgcolor: 'rgba(211,47,47,0.08)',
            '& .MuiCardHeader-avatar .MuiAvatar-root': {
              animation: 'stockMinusPulse 1.5s ease-in-out infinite',
            },
          }}
        />
        <CardContent sx={{ pt: 1.5, '&:last-child': { pb: 2 } }}>
          <Stack spacing={1}>
            {stockMinus.map((row, i) => {
              const qty = Number(row.quantity_balance ?? 0);
              return (
                <MDBox
                  key={`${row.code}-${row.barcode}-${i}`}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'rgba(244, 67, 53, 0.35)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Chip
                    size='small'
                    color='error'
                    icon={<Icon fontSize='small'>store</Icon>}
                    label={row.shortcode || '-'}
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    size='small'
                    variant='outlined'
                    icon={<Icon fontSize='small'>point_of_sale</Icon>}
                    label={row.c_name || '-'}
                    sx={{ height: 24 }}
                  />
                  <Chip
                    size='small'
                    variant='outlined'
                    icon={<Icon fontSize='small'>badge</Icon>}
                    label={row.code || '-'}
                    sx={{ height: 24 }}
                  />
                  <Chip
                    size='small'
                    variant='outlined'
                    icon={<Icon fontSize='small'>person</Icon>}
                    label={row.u_name || '-'}
                    sx={{ height: 24 }}
                  />
                  <MDBox flex={1} minWidth={180}>
                    <MDTypography variant='button' fontWeight='bold' display='block'>
                      {row.name || '-'}
                    </MDTypography>
                    {row.barcode ? (
                      <MDTypography variant='caption' color='text'>
                        {row.barcode}
                      </MDTypography>
                    ) : null}
                  </MDBox>
                  <Chip
                    size='small'
                    color='error'
                    variant='filled'
                    icon={<Icon fontSize='small'>inventory_2</Icon>}
                    label={`คงเหลือ ${Func.numberDisplay(qty)} ${row.unit_name || ''}`}
                    sx={{ fontWeight: 700, height: 28 }}
                  />
                </MDBox>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // ===========================================
  // Layout: top bar + sticky filter pills
  // ===========================================
  const renderTopBar = () => (
    <MDBox
      mb={2}
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      flexWrap='wrap'
      gap={1.5}
    >
      <MDBox>
        <MDTypography variant='h5' fontWeight='bold'>
          อนุมัติคำขอ POS
        </MDTypography>
        <MDTypography variant='button' color='text'>
          คำขอที่รออนุมัติทั้งหมด {total} รายการ
        </MDTypography>
      </MDBox>
      <Stack direction='row' spacing={1} alignItems='center'>
        <Chip
          size='small'
          color={safeChipColor(statusInfo.color)}
          icon={<Icon fontSize='small'>{statusInfo.icon}</Icon>}
          label={statusInfo.label}
          sx={{ fontWeight: 600 }}
        />
        <Tooltip title='เชื่อมต่อใหม่'>
          <span>
            <IconButton size='small' onClick={reconnect}>
              <Icon>refresh</Icon>
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </MDBox>
  );

  const renderFilterPills = () => {
    const tiles = [
      { key: 'all', icon: 'inbox', title: 'ทั้งหมด', color: 'primary', count: total },
      ...CATEGORY_KEYS.map((k) => ({
        key: k,
        icon: CATEGORY_META[k].icon,
        title: CATEGORY_META[k].title,
        color: CATEGORY_META[k].color,
        count: counts[k] || 0,
      })),
    ];

    return (
      <MDBox
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          py: 1.25,
          mb: 2,
          mx: -1,
          px: 1,
          backdropFilter: 'blur(8px)',
          bgcolor: 'rgba(255,255,255,0.85)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction='row'
          spacing={1.25}
          sx={{ overflowX: 'auto', pb: 0.5, px: 0.5, scrollbarWidth: 'thin' }}
        >
          {tiles.map((tile) => {
            const active = activeTab === tile.key;
            const accent = `${tile.color}.main`;
            return (
              <MDBox
                key={tile.key}
                onClick={() => setActiveTab(tile.key)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveTab(tile.key);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  flexShrink: 0,
                  minWidth: 150,
                  py: 1.1,
                  px: 1.5,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: active ? accent : 'divider',
                  bgcolor: active ? accent : 'background.paper',
                  color: active ? 'common.white' : 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
                  boxShadow: active ? 3 : 0,
                  outline: 'none',
                  userSelect: 'none',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2,
                    borderColor: accent,
                  },
                }}
              >
                <MDBox
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: active ? 'rgba(255,255,255,0.22)' : accent,
                    color: 'common.white',
                    flexShrink: 0,
                  }}
                >
                  <Icon fontSize='small'>{tile.icon}</Icon>
                </MDBox>
                <MDBox flex={1} minWidth={0}>
                  <MDTypography
                    variant='caption'
                    sx={{
                      color: 'inherit',
                      opacity: active ? 0.95 : 0.7,
                      display: 'block',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {tile.title}
                  </MDTypography>
                  <MDTypography
                    variant='h6'
                    fontWeight='bold'
                    sx={{ color: 'inherit', lineHeight: 1.1 }}
                  >
                    {tile.count}
                  </MDTypography>
                </MDBox>
                {tile.count > 0 && !active ? (
                  <MDBox
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: accent,
                      flexShrink: 0,
                    }}
                  />
                ) : null}
              </MDBox>
            );
          })}
        </Stack>
      </MDBox>
    );
  };

  // ===========================================
  // Card chrome (unified for every category)
  // ===========================================
  const renderActions = (category, payload, summary) => {
    const approveKey = `${category}:approve:${JSON.stringify(payload)}`;
    const rejectKey = `${category}:reject:${JSON.stringify(payload)}`;
    const isApproving = busyKey === approveKey;
    const isRejecting = busyKey === rejectKey;

    return (
      <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
        <MDButton
          size='small'
          color='error'
          variant='outlined'
          startIcon={
            isRejecting ? <CircularProgress size={14} color='inherit' /> : <Icon>close</Icon>
          }
          disabled={!!busyKey}
          onClick={() => askConfirm(category, 'reject', payload, summary)}
        >
          ปฏิเสธ
        </MDButton>
        <MDButton
          size='small'
          color='success'
          variant='gradient'
          startIcon={
            isApproving ? <CircularProgress size={14} color='inherit' /> : <Icon>check_circle</Icon>
          }
          disabled={!!busyKey}
          onClick={() => askConfirm(category, 'approve', payload, summary)}
        >
          อนุมัติ
        </MDButton>
      </Stack>
    );
  };

  const renderShiftChips = (shiftId, shiftInfo) => (
    <Stack direction='row' spacing={0.75} alignItems='center' flexWrap='wrap' useFlexGap>
      <Chip
        size='small'
        icon={<Icon fontSize='small'>badge</Icon>}
        label={`SHIFT #${shiftId}`}
        variant='outlined'
        sx={{ height: 24 }}
      />
      {shiftInfo?.branch_name || shiftInfo?.short_code ? (
        <Chip
          size='small'
          icon={<Icon fontSize='small'>store</Icon>}
          label={`${shiftInfo?.branch_name || shiftInfo?.short_code}${
            shiftInfo?.pos ? ` • ${shiftInfo.pos}` : ''
          }`}
          variant='outlined'
          sx={{ height: 24 }}
        />
      ) : null}
      {shiftInfo?.cashier_name ? (
        <Chip
          size='small'
          icon={<Icon fontSize='small'>person</Icon>}
          label={shiftInfo.cashier_name}
          variant='outlined'
          sx={{ height: 24 }}
        />
      ) : null}
    </Stack>
  );

  const renderBillLines = (lines, getName, getQty, getTotal) => (
    <MDBox
      sx={{
        bgcolor: 'grey.100',
        borderRadius: 1.5,
        p: 1.25,
        mt: 1.25,
        maxHeight: 180,
        overflow: 'auto',
      }}
    >
      {lines.map((line, idx) => (
        <Stack
          key={`bill-line-${idx}`}
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{ py: 0.4 }}
        >
          <Icon fontSize='small' color='disabled'>
            label
          </Icon>
          <MDTypography variant='caption' sx={{ flex: 1 }} noWrap>
            {getName(line) || '-'}
          </MDTypography>
          <Chip size='small' label={`x${getQty(line)}`} sx={{ height: 22 }} />
          <MDTypography
            variant='caption'
            fontWeight='bold'
            sx={{ minWidth: 80, textAlign: 'right' }}
          >
            ฿ {formatMoney(getTotal(line))}
          </MDTypography>
        </Stack>
      ))}
    </MDBox>
  );

  const renderRemark = (remark) =>
    remark ? (
      <Stack direction='row' spacing={1} alignItems='flex-start' mt={1}>
        <Icon fontSize='small' color='info'>
          sticky_note_2
        </Icon>
        <MDTypography variant='caption' color='text' sx={{ whiteSpace: 'pre-wrap' }}>
          {remark}
        </MDTypography>
      </Stack>
    ) : null;

  const renderCard = ({
    key,
    category,
    avatarIcon,
    avatarSrc,
    title,
    titleRight,
    subtitle,
    chips,
    extra,
    amount,
    amountColor,
    shiftId,
    shiftInfo,
    actionPayload,
    summary,
    highlight,
  }) => {
    const meta = CATEGORY_META[category];
    const accent = `${meta.color}.main`;
    return (
      <Card
        key={key}
        sx={{
          mb: 1.5,
          overflow: 'visible',
          borderLeft: highlight ? '6px solid' : '4px solid',
          borderColor: highlight ? 'error.main' : accent,
          ...(highlight && {
            borderStyle: 'solid',
            borderWidth: '2px',
            borderLeftWidth: '6px',
            borderColor: 'error.main',
          }),
          transition: 'box-shadow 0.15s, transform 0.15s',
          '&:hover': { boxShadow: 4 },
        }}
      >
        <MDBox p={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
          >
            <Avatar
              variant='rounded'
              src={avatarSrc}
              sx={{
                width: 56,
                height: 56,
                bgcolor: accent,
                color: 'white',
                flexShrink: 0,
              }}
            >
              {avatarIcon ? <Icon>{avatarIcon}</Icon> : null}
            </Avatar>

            <MDBox flex={1} minWidth={0}>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                flexWrap='wrap'
                useFlexGap
                mb={0.5}
              >
                <Chip
                  size='small'
                  color={safeChipColor(meta.color)}
                  icon={<Icon fontSize='small'>{meta.icon}</Icon>}
                  label={meta.title}
                  sx={{ height: 24, fontWeight: 600 }}
                />
                <MDTypography variant='button' fontWeight='bold' sx={{ wordBreak: 'break-word' }}>
                  {title}
                </MDTypography>
                {titleRight}
              </Stack>

              {subtitle ? (
                <MDTypography variant='caption' color='text' display='block' mb={0.75}>
                  {subtitle}
                </MDTypography>
              ) : null}

              {chips ? (
                <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap mb={1}>
                  {chips}
                </Stack>
              ) : null}

              {renderShiftChips(shiftId, shiftInfo)}

              {extra}
            </MDBox>

            <Stack
              direction={{ xs: 'row', md: 'column' }}
              alignItems={{ xs: 'center', md: 'flex-end' }}
              justifyContent={{ xs: 'space-between', md: 'flex-start' }}
              spacing={1.5}
              sx={{ minWidth: { md: 200 }, flexShrink: 0 }}
            >
              {amount ? (
                React.isValidElement(amount) ? (
                  amount
                ) : (
                  <MDTypography
                    variant='h6'
                    color={amountColor || 'text'}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {amount}
                  </MDTypography>
                )
              ) : (
                <MDBox />
              )}
              {renderActions(category, actionPayload, summary)}
            </Stack>
          </Stack>
        </MDBox>
      </Card>
    );
  };

  // ===========================================
  // Per-category renderers
  // ===========================================
  const renderDelCart = ({ shiftId, item }) => {
    const p = item.Payload || {};
    const product = p.Product || {};
    // QuantityBalance is sent only when the product's store stock is negative.
    const isNegativeStock = p.QuantityBalance != null && Number(p.QuantityBalance) < 0;
    const unit = product.Unit_name || '';
    return renderCard({
      key: `delcart-${shiftId}-${item.CartListId}`,
      category: 'DelCart',
      avatarIcon: 'delete_outline',
      avatarSrc: product.Image,
      highlight: isNegativeStock,
      title: product.Name || '-',
      titleRight: (
        <>
          {product.Barcode ? (
            <Chip
              size='small'
              variant='outlined'
              icon={<Icon fontSize='small'>qr_code_2</Icon>}
              label={product.Barcode}
              sx={{ height: 22 }}
            />
          ) : null}
          {isNegativeStock ? (
            <Chip
              size='small'
              color='error'
              icon={<Icon fontSize='small'>warning</Icon>}
              label={`สต็อกติดลบ: ${Func.numberDisplay(p.QuantityBalance)} ${unit}`}
              sx={{ height: 22, fontWeight: 700 }}
            />
          ) : null}
        </>
      ),
      chips: (
        <>
          <Chip
            size='small'
            icon={<Icon fontSize='small'>sell</Icon>}
            label={`฿ ${formatMoney(product.Price)}`}
            sx={{ height: 22 }}
          />
          <Chip
            size='small'
            icon={<Icon fontSize='small'>numbers</Icon>}
            label={`${p.Quantity} ${unit}`}
            sx={{ height: 22 }}
          />
        </>
      ),
      shiftId,
      shiftInfo: p.ShiftInfo,
      extra: isNegativeStock ? (
        <Alert
          severity='error'
          variant='outlined'
          icon={<Icon fontSize='small'>report</Icon>}
          sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.75 } }}
        >
          <MDTypography variant='caption' color='error' fontWeight='bold'>
            ขอลบสินค้าติดลบ — สต็อกคงเหลือ {Func.numberDisplay(p.QuantityBalance)} {unit}
          </MDTypography>
        </Alert>
      ) : null,
      amount: `฿ ${formatMoney(p.Total)}`,
      amountColor: 'error',
      actionPayload: { shiftId, cartListId: item.CartListId, approvalId: item.ApprovalId },
      summary: isNegativeStock
        ? `ลบสินค้าติดลบ ${product.Name} จำนวน ${p.Quantity} (สต็อก ${Func.numberDisplay(
            p.QuantityBalance
          )})`
        : `ลบ ${product.Name} จำนวน ${p.Quantity}`,
    });
  };

  const renderDiscount = ({ shiftId, item }) => {
    const p = item.Payload || {};
    const cart = p.Cart || {};
    const lines = Array.isArray(cart.cart_list) ? cart.cart_list : [];
    return renderCard({
      key: `discount-${shiftId}`,
      category: 'DiscountRequest',
      avatarIcon: 'discount',
      title: 'ขออนุมัติส่วนลด',
      chips: (
        <>
          <Chip
            size='small'
            icon={<Icon fontSize='small'>shopping_cart</Icon>}
            label={`รวม ฿ ${formatMoney(cart.Total)}`}
            sx={{ height: 22 }}
          />
          {Number(cart.Discount) > 0 ? (
            <Chip
              size='small'
              icon={<Icon fontSize='small'>redeem</Icon>}
              label={`โปรโมชั่น ฿ ${formatMoney(cart.Discount)}`}
              sx={{ height: 22 }}
            />
          ) : null}
          <Chip
            size='small'
            color='success'
            variant='outlined'
            icon={<Icon fontSize='small'>price_check</Icon>}
            label={`สุทธิ ฿ ${formatMoney(cart.Nettotal)}`}
            sx={{ height: 22 }}
          />
        </>
      ),
      shiftId,
      shiftInfo: p.ShiftInfo,
      extra:
        lines.length > 0
          ? renderBillLines(
              lines,
              (l) => l?.product?.Name,
              (l) => l?.quantity_all ?? l?.quantity,
              (l) => l?.total
            )
          : null,
      amount: `- ฿ ${formatMoney(p.Discount)}`,
      amountColor: 'warning',
      actionPayload: { shiftId, cartId: item.CartId, approvalId: item.ApprovalId },
      summary: `ส่วนลด ฿ ${formatMoney(p.Discount)}`,
    });
  };

  const renderCancel = ({ shiftId, item }) => {
    const p = item.Payload || {};
    const so = p.SaleOrder || {};
    const hasLines = Array.isArray(so.so_list) && so.so_list.length > 0;
    return renderCard({
      key: `cancel-${shiftId}`,
      category: 'CancelBill',
      avatarIcon: 'highlight_off',
      title: so.code || '-',
      titleRight: (
        <Chip
          size='small'
          icon={<Icon fontSize='small'>schedule</Icon>}
          label={formatDate(so.created_at)}
          variant='outlined'
          sx={{ height: 22 }}
        />
      ),
      shiftId,
      shiftInfo: p.ShiftInfo,
      extra: (
        <>
          {hasLines
            ? renderBillLines(
                so.so_list,
                (l) => l?.product?.Name,
                (l) => l?.quantity,
                (l) => l?.total
              )
            : null}
          {renderRemark(p.Remark)}
        </>
      ),
      amount: `฿ ${formatMoney(so.nettotal)}`,
      amountColor: 'error',
      actionPayload: { shiftId, cancelId: item.CancelId, approvalId: item.ApprovalId },
      summary: `ยกเลิกบิล ${so.code}`,
    });
  };

  const renderReturn = ({ shiftId, item }) => {
    const p = item.Payload || {};
    const ret = p.Return || {};
    const so = ret.SaleOrder || {};
    const list = ret.List || [];
    const calculatedTotal = list.reduce(
      (acc, l) => acc + (l?.Product?.Price || 0) * (l?.Quantity || 0),
      0
    );

    // ถ้า Payload.Return.Total ถูกส่งมาและ > 0 → ถือว่าผู้ขอ "กำหนดยอดคืนเอง"
    // ไม่ใช่ยอดรวมปกติจากการคิดราคา × จำนวนของแต่ละรายการ
    const requestedTotalRaw = ret.Total;
    const hasRequestedTotal =
      requestedTotalRaw !== undefined &&
      requestedTotalRaw !== null &&
      Number(requestedTotalRaw) > 0;
    const requestedTotal = hasRequestedTotal ? Number(requestedTotalRaw) : 0;
    const displayTotal = hasRequestedTotal ? requestedTotal : calculatedTotal;

    return renderCard({
      key: `return-${shiftId}`,
      category: 'ReturnBill',
      avatarIcon: 'change_circle',
      title: so.code || '-',
      titleRight: (
        <>
          <Chip
            size='small'
            icon={<Icon fontSize='small'>schedule</Icon>}
            label={formatDate(so.created_at)}
            variant='outlined'
            sx={{ height: 22 }}
          />
          {hasRequestedTotal ? (
            <Chip
              size='small'
              color='warning'
              icon={<Icon fontSize='small'>price_change</Icon>}
              label='ยอดคืนกำหนดเอง'
              sx={{ height: 22, fontWeight: 700 }}
            />
          ) : null}
        </>
      ),
      shiftId,
      shiftInfo: p.ShiftInfo,
      extra: (
        <>
          {list.length > 0
            ? renderBillLines(
                list,
                (l) => l?.Product?.Name,
                (l) => l?.Quantity,
                (l) => (l?.Product?.Price || 0) * (l?.Quantity || 0)
              )
            : null}
          {renderRemark(p.Remark)}
        </>
      ),
      amount: hasRequestedTotal ? (
        <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={0.25}>
          <MDTypography
            variant='caption'
            color='text'
            sx={{ textDecoration: 'line-through', opacity: 0.7, whiteSpace: 'nowrap' }}
          >
            เดิม ฿ {formatMoney(calculatedTotal)}
          </MDTypography>
          <MDTypography variant='h6' color='warning' sx={{ whiteSpace: 'nowrap' }}>
            ขอ ฿ {formatMoney(requestedTotal)}
          </MDTypography>
        </Stack>
      ) : (
        `฿ ${formatMoney(displayTotal)}`
      ),
      amountColor: 'info',
      actionPayload: { shiftId, returnId: item.ReturnId, approvalId: item.ApprovalId },
      summary: hasRequestedTotal
        ? `คืนบิล ${so.code} (เดิม ฿ ${formatMoney(calculatedTotal)} → ขอ ฿ ${formatMoney(
            requestedTotal
          )})`
        : `คืนสินค้าจากบิล ${so.code}`,
    });
  };

  const renderClose = ({ shiftId, item }) => {
    const p = item.Payload || {};
    const info = p.ShiftInfo || {};
    return renderCard({
      key: `close-${shiftId}`,
      category: 'CloseShift',
      avatarIcon: 'point_of_sale',
      avatarSrc: info.image,
      title: `รอบ ${info.shift_code || `#${shiftId}`}`,
      titleRight: info.date_record ? (
        <Chip
          size='small'
          icon={<Icon fontSize='small'>event</Icon>}
          label={info.date_record}
          variant='outlined'
          sx={{ height: 22 }}
        />
      ) : null,
      shiftId,
      shiftInfo: info,
      actionPayload: { shiftId, approvalId: item.ApprovalId },
      summary: `ปิดรอบ ${info.shift_code || shiftId}`,
    });
  };

  const RENDERERS = {
    DelCart: renderDelCart,
    DiscountRequest: renderDiscount,
    CancelBill: renderCancel,
    ReturnBill: renderReturn,
    CloseShift: renderClose,
  };

  // ===========================================
  // Feed (filtered)
  // ===========================================
  const renderEmpty = () => {
    const meta = activeTab === 'all' ? null : CATEGORY_META[activeTab];
    return (
      <MDBox
        sx={{
          textAlign: 'center',
          py: 6,
          px: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Icon sx={{ opacity: 0.35, fontSize: '3.5rem !important' }}>
          {meta?.emptyIcon || 'inbox'}
        </Icon>
        <MDTypography variant='h6' color='text' display='block' mt={1}>
          ไม่มีรายการรออนุมัติ
        </MDTypography>
        <MDTypography variant='caption' color='text'>
          {activeTab === 'all'
            ? 'ทุกอย่างเรียบร้อยดี ระบบจะแจ้งเตือนเมื่อมีคำขอใหม่เข้ามา'
            : `ไม่มีคำขอประเภท "${meta?.title || ''}" ในขณะนี้`}
        </MDTypography>
      </MDBox>
    );
  };

  const renderFeed = () => {
    const targets = activeTab === 'all' ? CATEGORY_KEYS : [activeTab];
    const items = [];
    targets.forEach((cat) => {
      lists[cat].forEach((entry) => items.push({ category: cat, ...entry }));
    });

    if (items.length === 0) return renderEmpty();

    return (
      <MDBox>
        {items.map((entry) => {
          const renderer = RENDERERS[entry.category];
          return renderer ? renderer(entry) : null;
        })}
      </MDBox>
    );
  };

  // ===========================================
  // Confirm dialog
  // ===========================================
  const renderConfirm = () => (
    <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth='xs' fullWidth>
      <DialogTitle>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Icon color={confirm?.action === 'approve' ? 'success' : 'error'}>
            {confirm?.action === 'approve' ? 'check_circle' : 'cancel'}
          </Icon>
          <span>ยืนยัน{confirm?.action === 'approve' ? 'อนุมัติ' : 'ปฏิเสธ'}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          คุณต้องการ{confirm?.action === 'approve' ? 'อนุมัติ' : 'ปฏิเสธ'}คำขอนี้?
        </DialogContentText>
        {confirm?.summary ? (
          <MDBox mt={1.5} p={1.5} sx={{ bgcolor: 'grey.100', borderRadius: 1 }}>
            <MDTypography variant='caption' color='text'>
              <Icon fontSize='inherit' sx={{ verticalAlign: 'middle', mr: 0.5 }}>
                info
              </Icon>
              {confirm.summary}
            </MDTypography>
          </MDBox>
        ) : null}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={() => setConfirm(null)} color='secondary' variant='text'>
          ยกเลิก
        </MDButton>
        <MDButton
          onClick={() => callAction(confirm.category, confirm.action, confirm.payload)}
          color={confirm?.action === 'approve' ? 'success' : 'error'}
          variant='gradient'
          disabled={!!busyKey}
          startIcon={
            busyKey ? (
              <CircularProgress size={14} color='inherit' />
            ) : (
              <Icon>{confirm?.action === 'approve' ? 'check' : 'close'}</Icon>
            )
          }
        >
          ยืนยัน
        </MDButton>
      </DialogActions>
    </Dialog>
  );

  // ===========================================
  // Active shifts (API)
  // ===========================================
  const renderShiftCard = (row) => (
    <Grid item xs={12} md={6} lg={3} xl={2} key={row.shift_code}>
      <MDBox className='card'>
        <CardHeader
          avatar={Func.imageDisplay(row)}
          title={
            <Tooltip title={row.pos || row.branch_name}>
              <MDTypography variant='subtitle2'>{row.branch_name}</MDTypography>
            </Tooltip>
          }
          subheader={
            <Tooltip title='ยอดเงินในลิ้นชัก'>
              <code className='color-info nowrap'>{Func.numberDisplay(row.wallet)} ฿</code>
            </Tooltip>
          }
        />
        <CardContent sx={{ px: 2, py: 0, fontSize: 15, color: '#666666', fontWeight: 100 }}>
          <Grid container>
            <Grid item xs={2}>
              <Icon>receipt_long</Icon>
            </Grid>
            <Grid item xs={10} className='text-right color-danger'>
              {Func.numberDisplay(row.bill_count)} บิล
            </Grid>
            <Grid item xs={2}>
              <Icon>point_of_sale</Icon>
            </Grid>
            <Grid item xs={10} className='text-right color-primary'>
              {row.pos === '' || row.pos == null ? '-' : row.pos}
            </Grid>
            <Grid item xs={2}>
              <Icon>date_range</Icon>
            </Grid>
            <Grid item xs={10} className='text-right'>
              {row.shift_code === '' || row.shift_code == null ? '-' : row.shift_code}
            </Grid>
            <Grid item xs={2}>
              <Icon>person</Icon>
            </Grid>
            <Grid item xs={10} className='text-right'>
              {row.cashier_name === '' || row.cashier_name == null ? '-' : row.cashier_name}
            </Grid>
            <Grid item xs={2}>
              <Icon>schedule</Icon>
            </Grid>
            <Grid item xs={10} className='text-right'>
              {Func.dateTimeDisplay(row.created_at)}
            </Grid>
          </Grid>
        </CardContent>
      </MDBox>
    </Grid>
  );

  const renderShiftsSection = () => (
    <Card sx={{ p: 2, mt: 4 }}>
      <MDBox mb={1}>
        <Grid container alignItems='center'>
          <Grid item xs={6}>
            <MDTypography variant='h5'>POS</MDTypography>
            <MDTypography variant='caption' color='text'>
              {displayShifts.length} รายการ
            </MDTypography>
          </Grid>
          <Grid item xs={6}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton
                variant='gradient'
                color='light'
                size='small'
                iconOnly
                onClick={loadShifts}
                disabled={loadingShifts}
              >
                {loadingShifts ? (
                  <CircularProgress size={18} color='inherit' />
                ) : (
                  <Icon>refresh</Icon>
                )}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <MDBox noValidate component='form' role='form' onSubmit={(e) => e.preventDefault()}>
            <MDInput
              name='shiftsearch'
              fullWidth
              type='search'
              value={shiftSearch}
              onChange={(e) => setShiftSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                ),
                autoComplete: 'off',
              }}
            />
          </MDBox>
        </Grid>
        {displayShifts.length === 0 ? (
          <Grid item xs={12}>
            <MDBox sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
              <Icon sx={{ fontSize: '3rem !important', opacity: 0.35 }}>storefront</Icon>
              <MDTypography variant='h6' display='block' mt={1}>
                ไม่พบรอบขายที่เปิดอยู่
              </MDTypography>
            </MDBox>
          </Grid>
        ) : (
          displayShifts.map(renderShiftCard)
        )}
      </Grid>
    </Card>
  );

  // แสดง Skeleton เฉพาะตอนต่อครั้งแรกที่ยังไม่เคยได้ snapshot
  // ถ้าเคยโหลดสำเร็จแล้วก็อย่ากระโดดกลับมา Skeleton ตอน reconnect (กันกระพริบ)
  const isInitialLoading = status === 'connecting' && total === 0 && !hasLoadedRef.current;

  return (
    <DashboardLayout>
      <Navbar route={[t('menu.dashboard'), t('menu.dashboard_pos_v2') || 'POS V2']} />
      <MDBox py={3}>
        {renderStockMinusBanner()}
        {renderTopBar()}
        {renderFilterPills()}
        {isInitialLoading ? (
          <MDBox>
            <Skeleton variant='rounded' height={120} sx={{ mb: 1.5 }} />
            <Skeleton variant='rounded' height={120} sx={{ mb: 1.5 }} />
            <Skeleton variant='rounded' height={120} />
          </MDBox>
        ) : (
          renderFeed()
        )}
        {renderShiftsSection()}
      </MDBox>
      <Footer />
      {renderConfirm()}
      {notify}
    </DashboardLayout>
  );
}

export default DashboardPosV2;
